// src/pages/Separation/SeparationManagement.tsx

import React, { useState } from 'react';
import { TabType, DashboardStats } from '../../types/separation';
import { getSeparationStats } from '../../api/separation';
import DashboardStatsComponent from '../../components/separation/dashboardstats';
import ResignationList from '../../components/separation/resignationlist';
import TerminationList from '../../components/separation/terminationlist';
import ExitChecklist from '../../components/separation/exitchecklist';
import SettlementModal from '../../components/separation/settlementmodal';

const SeparationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const [isSettlementOpen, setIsSettlementOpen] = useState(false);
  const [selectedSeparation, setSelectedSeparation] = useState<{ type: string; id: string; employeeId: string } | null>(null);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSeparationStats();
      setStats(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleViewChecklist = (type: string, id: string) => {
    setSelectedSeparation({ type, id, employeeId: '' });
    setIsChecklistOpen(true);
  };

  const handleCreateSettlement = (type: string, id: string, employeeId: string) => {
    setSelectedSeparation({ type, id, employeeId });
    setIsSettlementOpen(true);
  };

  const userRole = 'admin'; // Get from auth context

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Separation Management
        </h1>
        <p className="text-gray-500 mt-1">Manage resignations, terminations, exit checklists, and settlements</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-2 flex-wrap">
          {[
            { id: 'dashboard' as TabType, name: 'Dashboard', icon: '📊' },
            { id: 'resignations' as TabType, name: 'Resignations', icon: '📝' },
            { id: 'terminations' as TabType, name: 'Terminations', icon: '⚠️' },
            { id: 'checklists' as TabType, name: 'Exit Checklists', icon: '✅' },
            { id: 'settlements' as TabType, name: 'Settlements', icon: '💰' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'dashboard') loadStats();
              }}
              className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'dashboard' && (
          <DashboardStatsComponent stats={stats} loading={loading} error={error} onRefresh={loadStats} />
        )}
        
        {activeTab === 'resignations' && (
          <ResignationList
            onViewChecklist={handleViewChecklist}
            onCreateSettlement={handleCreateSettlement}
            userRole={userRole}
          />
        )}
        
        {activeTab === 'terminations' && (
          <TerminationList
            onViewChecklist={handleViewChecklist}
            onCreateSettlement={handleCreateSettlement}
            userRole={userRole}
          />
        )}
        
        {activeTab === 'checklists' && (
          <div className="space-y-4">
            <p className="text-gray-500 text-center py-8">Select a resignation or termination to view its checklist</p>
          </div>
        )}
        
        {activeTab === 'settlements' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <p className="text-gray-500 text-center py-8">Settlement list coming soon...</p>
          </div>
        )}
      </div>

      {/* Checklist Modal */}
      {isChecklistOpen && selectedSeparation && (
        <ExitChecklist
          separationType={selectedSeparation.type}
          separationId={selectedSeparation.id}
          isModal={true}
          onClose={() => {
            setIsChecklistOpen(false);
            setSelectedSeparation(null);
          }}
        />
      )}

      {/* Settlement Modal */}
      {isSettlementOpen && selectedSeparation && (
        <SettlementModal
          isOpen={isSettlementOpen}
          onClose={() => {
            setIsSettlementOpen(false);
            setSelectedSeparation(null);
          }}
          separationType={selectedSeparation.type}
          separationId={selectedSeparation.id}
          employeeId={selectedSeparation.employeeId}
          onSuccess={() => {
            setIsSettlementOpen(false);
            setSelectedSeparation(null);
          }}
        />
      )}
    </div>
  );
};

export default SeparationManagement;